import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, Globe, Target, Shield, Handshake, MapPin, Euro, HandHeart, CheckCircle } from 'lucide-react';

const AboutEN = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentPersonSlide, setCurrentPersonSlide] = useState(0);
  const [currentSchoolSlide, setCurrentSchoolSlide] = useState(0);

  // Team members
  const teamMembers = [
    {
      name: "Stefan",
      image: "/images/stefan-updated.png",
      alt: "Stefan - Board Member"
    },
    {
      name: "Thomas",
      image: "https://customer-assets.emergentagent.com/job_a34eb1ed-43a6-4ee4-a791-0955c92643a8/artifacts/kexsi7jw_2.png",
      alt: "Thomas - Board Member"
    },
    {
      name: "Daniel",
      image: "https://customer-assets.emergentagent.com/job_a34eb1ed-43a6-4ee4-a791-0955c92643a8/artifacts/vb0xk9ev_3.png",
      alt: "Daniel - Board Member"
    },
    {
      name: "Nadja",
      image: "/images/nadja-new.png",
      alt: "Nadja - Board Member"
    }
  ];

  // Gaza images from Imgur
  const gazaImages = [
    {
      src: "https://i.imgur.com/g0cPIxY.jpg",
      alt: "Gaza - Children and Hope",
      description: "For over two years, children have been lining up for a meal."
    },
    {
      src: "https://i.imgur.com/NNCetKb.jpg", 
      alt: "Gaza - Life in War",
      description: "Streets have become fields of rubble – children in the midst of it."
    },
    {
      src: "https://i.imgur.com/PkJ3G4R.jpg",
      alt: "Gaza - Destruction and Courage",
      description: "A child sits where their home once stood."
    },
    {
      src: "/images/gaza-destruction-new.jpg",
      alt: "Gaza - Daily Life in Emergency",
      description: "An entire residential area: in ruins after months of attacks."
    },
    {
      src: "/images/gaza-people-lost-everything.jpg",
      alt: "Gaza - People Have Lost Everything",
      description: "Among destroyed houses and cars, people search for food."
    },
    {
      src: "/images/gaza-destruction-rubble.jpg",
      alt: "Gaza - Destruction and Rubble",
      description: "After the destruction, the arduous reconstruction begins each time."
    },
    {
      src: "https://customer-assets.emergentagent.com/job_hayforhumanity/artifacts/vosjfvo3_mohammed-ibrahim-703Sobdug5o-unsplash.jpg",
      alt: "Gaza - Reconstruction After Destruction",
      description: "Childhood among rubble and clotheslines – everyday life in war."
    }
  ];

  // School reconstruction project images
  const schoolImages = [
    {
      src: "/images/school-1.jpg",
      alt: "School after bombing",
      description: "The school was destroyed in a bombing in 2025."
    },
    {
      src: "/images/school-2.jpg",
      alt: "School before destruction",
      description: "A daily life of safety and learning was no longer imaginable."
    },
    {
      src: "/images/school-3.jpg",
      alt: "School reconstruction",
      description: "With our support, the school is being rebuilt."
    },
    {
      src: "/images/school-4.jpg",
      alt: "Construction work at the school",
      description: "Our contacts on the ground work with endurance and craftsmanship under difficult conditions."
    },
    {
      src: "/images/school-5.jpg",
      alt: "Children in school",
      description: "Children are coming back to school."
    },
    {
      src: "/images/school-6.jpg",
      alt: "Hope and perspective",
      description: "A piece of everyday life, hope and perspective are back."
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % gazaImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + gazaImages.length) % gazaImages.length);
  };

  const nextPersonSlide = () => {
    setCurrentPersonSlide((prev) => (prev + 1) % teamMembers.length);
  };

  const prevPersonSlide = () => {
    setCurrentPersonSlide((prev) => (prev - 1 + teamMembers.length) % teamMembers.length);
  };

  const nextSchoolSlide = () => {
    setCurrentSchoolSlide((prev) => (prev + 1) % schoolImages.length);
  };

  const prevSchoolSlide = () => {
    setCurrentSchoolSlide((prev) => (prev - 1 + schoolImages.length) % schoolImages.length);
  };

  // Auto-advance slider
  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, []);

  // Auto-advance person slider
  useEffect(() => {
    const personTimer = setInterval(nextPersonSlide, 4000);
    return () => clearInterval(personTimer);
  }, []);

  // Auto-advance school slider
  useEffect(() => {
    const schoolTimer = setInterval(nextSchoolSlide, 5000);
    return () => clearInterval(schoolTimer);
  }, []);

  const values = [
    {
      icon: Heart,
      title: "Humanity",
      description: "We are driven by pure humanity,<br />not a political agenda."
    },
    {
      icon: Shield,
      title: "Transparency",
      description: "Every donation is documented. We regularly provide accountability."
    },
    {
      icon: Handshake,
      title: "Direct Aid",
      description: "Quick help for people in acute emergencies. Unbureaucratic and direct."
    }
  ];

  const achievements = [
    {
      icon: Euro,
      number: "30,000+",
      text: "Euros already collected and deployed directly on the ground.",
      link: "https://gofundme.com/f/soforthilfe-fuer-gaza" // GoFundMe Link
    },
    {
      icon: Users,
      number: "200+",
      text: "Families supported in Rafah, Khan Younis, and Gaza City."
    },
    {
      icon: HandHeart,
      number: "100%",
      text: "Transparency through documentation of every aid measure."
    }
  ];

  const helpMeasures = [
    "Food for families in Rafah, Khan Younis, and Gaza City.",
    "Medicine for chronically ill people.",
    "Tents and hygiene supplies for internally displaced people.",
    "Targeted individual case assistance (e.g., for medical emergencies and prostheses)."
  ];

  return (
    <section id="about" className="py-20 bg-cream">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header mit Logo - Desktop: Text links, Logo rechts */}
          <motion.div
            className="grid lg:grid-cols-2 gap-12 items-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {/* Text Section - Links */}
            <div className="text-center lg:text-left">
              <h2 className="text-5xl md:text-6xl font-bold text-charcoal mb-6 font-heading">
                Emergency Relief for <span className="text-olive-green">Gaza</span>.
              </h2>
              <div className="w-24 h-1 bg-olive-green mx-auto lg:mx-0 mb-6"></div>
              <p className="text-xl text-charcoal/80 leading-relaxed">
                We enable what currently seems nearly impossible: We bring aid to where it barely reaches - directly, personally, through our family on the ground. Since we are affected ourselves, we have had to learn over the past years how support actually reaches Gaza - and we can now use that knowledge and those channels. Thus, we create help that connects. Hope that remains. Together with you. For more humanity.
              </p>
            </div>

            {/* Logo Section - Rechts */}
            <div className="flex justify-center lg:justify-end">
              <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-olive-green/10">
                <img
                  src="/images/hay-logo-english.png"
                  alt="hay - humanity. action. you. Logo"
                  className="w-64 h-auto opacity-95"
                />
              </div>
            </div>
          </motion.div>

          {/* School Reconstruction Project */}
          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-charcoal font-heading mb-6">Our Current Project: <span className="text-olive-green">Rebuilding a School.</span></h3>
              <div className="max-w-4xl mx-auto mb-8">
                <p className="text-lg text-charcoal/80 leading-relaxed">
                  Schools are places of safety, education, and hope for the future. In 1995, our family helped build a school in Gaza. Over the past two and a half years, it has not only been a place of learning, but also a refuge for families. In 2025, the building was destroyed by bombs. For the past several months, our partners on the ground have been rebuilding the school with our financial support. We have already made significant progress: classes can now take place regularly again. The children come together, learn together, play together, and receive meals. A beginning has been made, but the reconstruction is far from complete. Despite the difficult circumstances, we continue moving forward. Our vision is clear: to provide 900 children with access to education and the chance for a sense of normal everyday life. Thank you for walking this path with us.
                </p>
              </div>
            </div>
            
            {/* School Project Image Slider */}
            <div className="relative max-w-4xl mx-auto">
              <div className="overflow-hidden rounded-2xl shadow-2xl">
                <motion.div
                  className="flex"
                  animate={{ x: `${-currentSchoolSlide * 100}%` }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                >
                  {schoolImages.map((image, index) => (
                    <div key={index} className="min-w-full relative">
                      <img
                        src={image.src}
                        alt={image.alt}
                        className={`w-full h-96 object-cover ${index === 4 ? 'object-bottom md:object-[center_80%]' : ''} ${index === 5 ? 'object-bottom md:object-[center_85%]' : ''}`}
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                        <p className="text-white text-lg font-medium">{image.description}</p>
                      </div>
                    </div>
                  ))}
                </motion.div>
              </div>
              
              {/* Slider Controls */}
              <div className="flex justify-center mt-6 space-x-2">
                {schoolImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSchoolSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSchoolSlide ? 'bg-olive-green scale-125' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
              
              {/* Navigation Arrows */}
              <button
                onClick={prevSchoolSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextSchoolSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </motion.div>

          {/* Gaza Reality Image Slider */}
          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-charcoal font-heading mb-6">The Reality in Gaza.</h3>
              <div className="max-w-4xl mx-auto mb-8">
                <p className="text-lg text-charcoal/80 leading-relaxed">
                  Since the beginning of the war, life in the Gaza Strip has changed dramatically. <a href="https://en.wikipedia.org/wiki/Casualties_of_the_Gaza_war" target="_blank" rel="noopener noreferrer" className="text-olive-green hover:text-olive-dark underline transition-colors">Nearly 75,000 people have been killed</a>, <a href="https://news.un.org/en/story/2025/10/1166023" target="_blank" rel="noopener noreferrer" className="text-olive-green hover:text-olive-dark underline transition-colors">over half of the hospitals are destroyed</a>, <a href="https://www.undp.org/stories/clearing-most-rubble-gaza-strip-possible-seven-years-under-right-conditions" target="_blank" rel="noopener noreferrer" className="text-olive-green hover:text-olive-dark underline transition-colors">nearly 80% of buildings have been destroyed</a>, <a href="https://www.savethechildren.net/blog/education-under-attack-gaza-nearly-90-school-buildings-damaged-or-destroyed" target="_blank" rel="noopener noreferrer" className="text-olive-green hover:text-olive-dark underline transition-colors">over 90% of schools are damaged or destroyed</a>, <a href="https://www.fao.org/newsroom/detail/gaza-strip--98.5-percent-of-cropland-unavailable-for-cultivation-as-famine-looms/en" target="_blank" rel="noopener noreferrer" className="text-olive-green hover:text-olive-dark underline transition-colors">over 98% of farmland is damaged</a>. The United Nations speaks of the worst devastation of a region since 1945. Millions of people have been displaced and had to flee multiple times, often without safe refuge, without medical care, without food or drinking water. <a href="https://www.ohchr.org/sites/default/files/documents/hrbodies/hrcouncil/sessions-regular/session60/advance-version/a-hrc-60-crp-3.pdf" target="_blank" rel="noopener noreferrer" className="text-olive-green hover:text-olive-dark underline transition-colors">According to the independent Commission of Inquiry of the UN Human Rights Council, Israel is committing genocide in Gaza</a>.
                </p>
              </div>
            </div>
            
            <div className="relative max-w-4xl mx-auto">
              <div className="overflow-hidden rounded-2xl shadow-2xl">
                <motion.div
                  className="flex"
                  animate={{ x: `${-currentSlide * 100}%` }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                >
                  {gazaImages.map((image, index) => (
                    <div key={index} className="min-w-full relative">
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-96 object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                        <p className="text-white text-lg font-medium">{image.description}</p>
                      </div>
                    </div>
                  ))}
                </motion.div>
              </div>
              
              {/* Slider Controls */}
              <div className="flex justify-center mt-6 space-x-2">
                {gazaImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSlide ? 'bg-olive-green scale-125' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
              
              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </motion.div>

          {/* Who we are - Personal Story */}
          <motion.div
            id="who-we-are"
            className="mb-20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-3xl shadow-2xl p-12 border border-olive-green/20">
              <h3 className="text-4xl font-bold text-charcoal font-heading mb-8 text-center">
                Who we are.
              </h3>
              
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div className="space-y-6">
                  <p className="text-lg text-charcoal/80 leading-relaxed">
                    We are <strong>Stefan, Thomas, Daniel, and Nadja</strong> – siblings, children of a Palestinian-German family – and deeply connected to the lives and suffering of the people in Gaza.
                  </p>
                  
                  {/* Team Member Slider */}
                  <div className="my-8">
                    <div className="relative max-w-[200px] mx-auto">
                      <div className="overflow-hidden rounded-xl shadow-lg">
                        <motion.div
                          className="flex"
                          animate={{ x: `${-currentPersonSlide * 100}%` }}
                          transition={{ duration: 0.6, ease: "easeInOut" }}
                        >
                          {teamMembers.map((member, index) => (
                            <div key={index} className="min-w-full">
                              <img
                                src={member.image}
                                alt={member.alt}
                                className="w-full h-auto object-contain"
                              />
                            </div>
                          ))}
                        </motion.div>
                      </div>
                      
                      {/* Slider Controls */}
                      <div className="flex justify-center mt-3 space-x-2">
                        {teamMembers.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentPersonSlide(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              index === currentPersonSlide ? 'bg-olive-green scale-125' : 'bg-gray-300 hover:bg-gray-400'
                            }`}
                          />
                        ))}
                      </div>
                      
                      {/* Navigation Arrows */}
                      <button
                        onClick={prevPersonSlide}
                        className="absolute left-1 top-1/2 transform -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-1.5 rounded-full transition-all"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={nextPersonSlide}
                        className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-1.5 rounded-full transition-all"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-lg text-charcoal/80 leading-relaxed">
                    What drives us is not theory. It is the voices, faces, and stories of our own family: aunts, uncles, cousins who fight for survival day by day. Family members who were killed. People whose homes have been destroyed, whose future is without perspective, whose everyday life is marked by fear, hunger, and loss.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-olive-green to-olive-dark text-white p-6 rounded-xl">
                    {/* Tante Yusra Story - moved to top on desktop */}
                    <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg border-l-4 border-white/50 mb-6">
                      <p className="text-white/95 leading-relaxed italic">
                        Our Aunt Yusra was a teacher with heart and soul – her school now lies in ruins. Our Uncle Mustafa - once full of joy - now lies buried under rubble today. Our cousins, along with their families, once so carefree, now freeze at every sound in the night. Their stories are not headlines. They are our reality.
                      </p>
                    </div>
                    <p className="text-xl font-bold text-center mb-3">
                      We cannot look away.
                    </p>
                    <p className="text-white/90 leading-relaxed mb-6">
                      As people who live between two worlds – safe in Germany, with our hearts and full of worry in Gaza – we feel a responsibility. One that is greater than just our family. From this responsibility, "hay - humanity. action. you. e.V." was born: A non-profit association for humanitarian aid and solidarity.
                    </p>
                    <div className="text-center">
                      <button
                        onClick={() => {
                          const element = document.getElementById('donate');
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }}
                        className="bg-white text-olive-green hover:bg-cream px-8 py-3 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-lg"
                      >
                        Donate now
                      </button>
                    </div>
                  </div>

                  <p className="text-lg text-charcoal/80 leading-relaxed">
                    Already in the past we have helped together – for example during the Ukraine war. Our motivation remains the same: To help people who have no one right now. Nationalities don't matter, political positions don't matter. What matters is humanity.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Our Conviction - moved under "Who we are" */}
          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-4xl font-bold text-charcoal text-center mb-12 font-heading">
              Our Conviction.
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  className="bg-white p-8 rounded-2xl shadow-xl text-center group hover:shadow-2xl transition-all hover:scale-105"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="w-16 h-16 bg-olive-green/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-olive-green/20 transition-colors">
                    <value.icon className="w-8 h-8 text-olive-green" />
                  </div>
                  <h4 className="text-2xl font-bold text-charcoal mb-4 font-heading">{value.title}</h4>
                  <p 
                    className="text-charcoal/70 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: value.description }}
                  ></p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Achievements */}
          <motion.div
            id="achievements"
            className="mb-20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-4xl font-bold text-charcoal text-center mb-12 font-heading">
              What we have already achieved.
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              {achievements.map((achievement, index) => 
                achievement.link ? (
                  <motion.a
                    key={achievement.number}
                    href={achievement.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-white p-8 rounded-2xl shadow-xl text-center group hover:shadow-2xl transition-all hover:scale-105 cursor-pointer"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-16 h-16 bg-olive-green/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-olive-green/20 transition-colors">
                      <achievement.icon className="w-8 h-8 text-olive-green" />
                    </div>
                    <div className="text-3xl font-bold text-olive-green mb-3">{achievement.number}</div>
                    <p className="text-charcoal/80 leading-relaxed text-sm">{achievement.text}</p>
                    <p className="text-olive-green text-xs mt-2 font-semibold">→ To the GoFundMe campaign</p>
                  </motion.a>
                ) : (
                  <motion.div
                    key={achievement.number}
                    className="bg-white p-8 rounded-2xl shadow-xl text-center group hover:shadow-2xl transition-all hover:scale-105"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-16 h-16 bg-olive-green/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-olive-green/20 transition-colors">
                      <achievement.icon className="w-8 h-8 text-olive-green" />
                    </div>
                    <div className="text-3xl font-bold text-olive-green mb-3">{achievement.number}</div>
                    <p className="text-charcoal/80 leading-relaxed text-sm">{achievement.text}</p>
                  </motion.div>
                )
              )}
            </div>
          </motion.div>



          {/* How we help */}
          <motion.div
            id="how-we-help"
            className="mb-20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-olive-green to-olive-dark rounded-3xl p-12 text-white">
              <div className="text-center mb-8">
                <h3 className="text-4xl font-bold font-heading mb-6">
                  How we help.
                </h3>
              </div>
              
              <div className="grid lg:grid-cols-2 gap-8 items-center mb-12">
                <div className="space-y-6">
                  <p className="text-xl text-white/90 leading-relaxed">
                    We work closely with people on the ground in Gaza and Egypt whom we <strong>know personally and trust</strong>.
                  </p>
                  <p className="text-lg text-white/90 leading-relaxed">
                    We as a family have already donated tens of thousands of euros from our own funds and accompanied their targeted use – from escape assistance cases to supplying entire families. This way we ensure that every aid reaches exactly where it is most urgently needed.
                  </p>
                </div>
                
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-8">
                  <h4 className="text-2xl font-bold mb-6 text-center">Previous Aid Measures.</h4>
                  <div className="space-y-4 mb-8">
                    {helpMeasures.map((measure, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-1" />
                        <p className="text-white/90 leading-relaxed text-sm">{measure}</p>
                      </div>
                    ))}
                  </div>
                  <div className="text-center">
                    <button
                      onClick={() => {
                        const element = document.getElementById('donate');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      className="bg-white text-olive-green hover:bg-cream px-8 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-xl"
                    >
                      Donate now
                    </button>
                  </div>
                </div>
              </div>

              {/* Three Goals moved from "Was wir tun" */}
              <div className="grid lg:grid-cols-3 gap-8">
                <motion.div
                  className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 group hover:bg-white/30 transition-all hover:scale-105"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-white/30 transition-colors">
                    <Heart className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-4 font-heading text-center">1. Emergency Relief</h4>
                  <p className="text-white/90 leading-relaxed text-center">
                    We provide the funds for necessary relief supplies – water, food, medicine. Through direct contacts on the ground, we can deliver aid exactly where it is most urgently needed.
                  </p>
                </motion.div>

                <motion.div
                  className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 group hover:bg-white/30 transition-all hover:scale-105"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-white/30 transition-colors">
                    <MapPin className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-4 font-heading text-center">2. Escape Assistance</h4>
                  <p className="text-white/90 leading-relaxed text-center">
                    Currently, escape is unfortunately not possible. Should this become feasible again, we will support people who want to flee from the war zone – by financing transportation or necessary documents.
                  </p>
                </motion.div>

                <motion.div
                  className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 group hover:bg-white/30 transition-all hover:scale-105"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-white/30 transition-colors">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-4 font-heading text-center">3. New Beginning & Reconstruction</h4>
                  <p className="text-white/90 leading-relaxed text-center">
                    For people who were able to flee – and for those who (must) remain in Gaza – we want to create prospects: emergency shelters, educational opportunities, work, long-term help.
                  </p>
                </motion.div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default AboutEN;